import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Building2, MonitorSmartphone, Store, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { SUBSCRIPTION_STATUSES, type PlatformOverview, apiError } from '../lib';

interface Health {
  status: string;
  info?: Record<string, { status: string }>;
  error?: Record<string, { status: string }>;
}

/** The super-admin's top row: how big the platform is right now. */
export default function Overview() {
  const { t } = useTranslation();
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<PlatformOverview>('/platform/overview')
      .then(({ data: payload }) => setData(payload))
      .catch((e) => setError(apiError(e, t('platform.overview.loadFailed'))))
      .finally(() => setLoading(false));

    // `/health` is @Public() and answers outside the envelope, so a failure
    // here is itself the signal — surface it rather than swallowing it.
    api
      .get<Health>('/health')
      .then(({ data: payload }) => {
        setHealth(payload);
        setHealthError(null);
      })
      .catch((e) => {
        setHealth(null);
        setHealthError(apiError(e, t('platform.overview.healthDown')));
      });
  }, [t]);

  useEffect(load, [load]);

  const tiles = [
    { key: 'tenants', icon: Store, value: data?.totals.tenants },
    { key: 'liveSubscriptions', icon: Activity, value: data?.totals.liveSubscriptions },
    { key: 'branches', icon: Building2, value: data?.totals.branches },
    { key: 'users', icon: Users, value: data?.totals.users },
    { key: 'devices', icon: MonitorSmartphone, value: data?.totals.devices },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{t('nav.overview')}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{t('platform.overview.subtitle')}</p>
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {tiles.map(({ key, icon: Icon, value }) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm font-medium text-[var(--muted-foreground)]">
                    <Icon className="h-4 w-4" />
                    {t(`platform.overview.tile.${key}`)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold">{value ?? 0}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('platform.overview.byStatus')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              {SUBSCRIPTION_STATUSES.map((status) => (
                <div key={status} className="rounded-lg border p-3">
                  <Badge tone={toneForStatus(status)}>
                    {t(`platform.subscriptionStatus.${status}`)}
                  </Badge>
                  <p className="mt-1 text-2xl font-semibold">
                    {data?.subscriptionsByStatus?.[status] ?? 0}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.overview.health')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {healthError ? (
            <p className="rounded-lg bg-red-50 p-3 text-red-700">{healthError}</p>
          ) : !health ? (
            <p className="text-[var(--muted-foreground)]">{t('common.loading')}</p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Badge tone={health.status === 'ok' ? 'success' : 'danger'}>{health.status}</Badge>
                <span className="text-[var(--muted-foreground)]">
                  {t('platform.overview.healthChecks')}
                </span>
              </div>
              <ul className="divide-y rounded-lg border">
                {Object.entries({ ...(health.info ?? {}), ...(health.error ?? {}) }).map(
                  ([name, entry]) => (
                    <li key={name} className="flex items-center justify-between gap-2 p-2.5">
                      <span>{name}</span>
                      <Badge tone={entry.status === 'up' ? 'success' : 'danger'}>
                        {entry.status}
                      </Badge>
                    </li>
                  ),
                )}
              </ul>
            </>
          )}
          <a
            className="inline-block text-[var(--accent)] underline"
            href={`${import.meta.env.VITE_API_URL ?? '/api'}/health`}
            target="_blank"
            rel="noreferrer"
          >
            {t('platform.overview.rawHealth')}
          </a>
        </CardContent>
      </Card>
    </div>
  );
}
